/**
 * Decides whether a saved profile, read back from storage, really carries the
 * setup a revision was verified against. A save reporting success is not
 * evidence: only the re-fetched profile is.
 *
 * Plain data in, verdict out - no DTO, client or store shapes cross this line.
 */

import {
  areSetupFingerprintsEqual,
  type ConnectorParameterBindingKind,
  type ConnectorParameterDestination,
} from './setupFingerprint';

/** The connector target a profile binds parameters to. */
export interface ProfileConnectorTarget {
  destination: ConnectorParameterDestination;
  bindingKind: ConnectorParameterBindingKind;
}

/** What the revision saved, and therefore what the read-back must repeat. */
export interface ProfileReadBackExpectation {
  revisionId: number;
  target: ProfileConnectorTarget;
  setupFingerprint: string;
}

/** What the re-fetched profile actually turned out to hold. */
export interface ProfileReadBackObservation {
  /** `null` when the saved profile carries no interpretable connector setup. */
  target: ProfileConnectorTarget | null;
  setupFingerprint: string | null;
}

export type ProfileReadBackMismatchReason =
  /** The setup that was saved held no connector target to verify against. */
  | 'setup-unverifiable'
  | 'profile-unreadable'
  | 'destination-mismatch'
  | 'binding-kind-mismatch'
  | 'fingerprint-mismatch';

export interface ProfileReadBackMismatch {
  kind: 'mismatch';
  reason: ProfileReadBackMismatchReason;
  message: string;
}

export type ProfileReadBackVerdict =
  | { kind: 'verified' }
  | { kind: 'superseded' }
  | ProfileReadBackMismatch;

const RetryHint = 'Save the setup again to retry.';

const mismatchMessages: Record<ProfileReadBackMismatchReason, string> = {
  'setup-unverifiable': `The setup that was saved carried no connector parameter target, so the save could not be verified and was not trusted. Complete the connector parameter step, then save again.`,
  'profile-unreadable': `The saved profile was read back without any connector parameter setup, so it was not trusted. ${RetryHint}`,
  'destination-mismatch': `The saved profile was read back with a different parameter destination than the one just applied, so it was not trusted. ${RetryHint}`,
  'binding-kind-mismatch': `The saved profile was read back with a different parameter binding kind than the one just applied, so it was not trusted. ${RetryHint}`,
  'fingerprint-mismatch': `The saved profile was read back with different setup values than the ones just applied, so it was not trusted. ${RetryHint}`,
};

/** The single place a mismatch verdict, and its English, is produced. */
export const profileReadBackMismatch = (
  reason: ProfileReadBackMismatchReason
): ProfileReadBackMismatch => ({
  kind: 'mismatch',
  reason,
  message: mismatchMessages[reason],
});

/** The first reason the observation fails to repeat the expectation. */
const findMismatchReason = (
  expected: ProfileReadBackExpectation,
  observed: ProfileReadBackObservation
): ProfileReadBackMismatchReason | null => {
  if (!observed.target) {
    return 'profile-unreadable';
  }
  if (observed.target.destination !== expected.target.destination) {
    return 'destination-mismatch';
  }
  if (observed.target.bindingKind !== expected.target.bindingKind) {
    return 'binding-kind-mismatch';
  }
  if (!areSetupFingerprintsEqual(expected.setupFingerprint, observed.setupFingerprint)) {
    return 'fingerprint-mismatch';
  }
  return null;
};

/**
 * The claim a save makes about what storage must then hold.
 *
 * Returns `null` when the applied setup carries nothing a read-back could
 * confirm - no connector target, or no fingerprint to compare. That is not a
 * pass: with no claim to check, nothing can be verified, and the caller must
 * treat it as `setup-unverifiable` rather than as agreement.
 */
export const expectProfileReadBack = (
  applied: ProfileReadBackObservation,
  revisionId: number
): ProfileReadBackExpectation | null => {
  if (!applied.target || !applied.setupFingerprint) {
    return null;
  }

  return {
    revisionId,
    target: applied.target,
    setupFingerprint: applied.setupFingerprint,
  };
};

/**
 * A read-back only decides anything for the revision that is still current: a
 * response arriving after a newer revision started is discarded whatever it
 * says, so a late failure can never report against work it did not perform.
 */
export const verifyProfileReadBack = (
  expected: ProfileReadBackExpectation,
  observed: ProfileReadBackObservation,
  currentRevisionId: number
): ProfileReadBackVerdict => {
  if (expected.revisionId !== currentRevisionId) {
    return { kind: 'superseded' };
  }

  const reason = findMismatchReason(expected, observed);
  return reason ? profileReadBackMismatch(reason) : { kind: 'verified' };
};
